import React, { useState, useEffect, useCallback } from "react";
import { Row, Table, Tag, Drawer, DatePicker, Button, Modal, Space, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { formatDateTime } from "../../helpers/date";
import {
  formatAmount,
  pivotChargesByConsignment,
  chargeColumns,
  downloadStatementPdf,
  downloadStatementExcel,
  getDisplayStatus,
  DISPLAY_STATUS_COLORS,
  DISPLAY_STATUS_LABELS,
} from "../../helpers/billingStatement";
import { BILLING_TYPE_LABELS } from "../../constants/billing";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const UserBillingStatements = ({ merchantId }) => {
  const { permissions } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statementCharges, setStatementCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [generatePeriod, setGeneratePeriod] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!merchantId) return;

    setLoading(true);
    try {
      const res = await axios.get("/api/billing-statements", {
        params: { merchantId, limit: 100 },
      });
      setItems(res.data.data);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (!selected) {
      setStatementCharges([]);
      return;
    }

    setChargesLoading(true);
    axios
      .get(`/api/billing-statements/${selected.objectId}/charges`)
      .then((res) => setStatementCharges(pivotChargesByConsignment(res.data.data)))
      .catch((error) => message.error(error.response?.data?.message || error.message))
      .finally(() => setChargesLoading(false));
  }, [selected]);

  const handleGenerate = async () => {
    if (!generatePeriod) {
      message.warning("Billing period is required");
      return;
    }

    setGenerating(true);
    try {
      await axios.post("/api/billing-statements/generate", {
        merchantId,
        billingPeriod: generatePeriod.format("YYYY-MM"),
      });
      message.success("Statement generated");
      setGenerateOpen(false);
      setGeneratePeriod(null);
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setGenerating(false);
    }
  };

  const columns = [
    { title: "Statement No.", dataIndex: "statementNumber", key: "statementNumber" },
    { title: "Billing Period", dataIndex: "billingPeriod", key: "billingPeriod" },
    {
      title: "Billing Type",
      dataIndex: "billingType",
      key: "billingType",
      render: (t) => BILLING_TYPE_LABELS[t] || t || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => {
        const displayStatus = getDisplayStatus(record);
        return <Tag color={DISPLAY_STATUS_COLORS[displayStatus]}>{DISPLAY_STATUS_LABELS[displayStatus]}</Tag>;
      },
    },
    {
      title: "Issued",
      dataIndex: "issuedAt",
      key: "issuedAt",
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Due",
      dataIndex: "dueAt",
      key: "dueAt",
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Original Amount",
      key: "originalAmount",
      render: (_, r) => formatAmount(r.originalAmount, r.currency),
    },
    {
      title: "Outstanding",
      key: "outstandingAmount",
      render: (_, r) => formatAmount(r.outstandingAmount, r.currency),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <a onClick={() => setSelected(record)}>View</a>
          <a onClick={() => downloadStatementPdf(record.objectId)}>PDF</a>
          <a onClick={() => downloadStatementExcel(record.objectId)}>Excel</a>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {permissions?.includes(PERMISSIONS.GENERATE_BILLING_STATEMENT) && (
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateOpen(true)}>
            Generate Statement
          </Button>
        </Row>
      )}

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{ emptyText: "No billing statements yet" }}
      />

      <Drawer
        title="Billing statement detail"
        visible={!!selected}
        onClose={() => setSelected(null)}
        width={960}
      >
        {selected && (
          <div>
            <Space style={{ marginBottom: 16 }}>
              <Button type="primary" onClick={() => downloadStatementPdf(selected.objectId)}>
                Download PDF
              </Button>
              <Button onClick={() => downloadStatementExcel(selected.objectId)}>
                Download Excel
              </Button>
            </Space>
            <p><strong>Statement No.:</strong> {selected.statementNumber}</p>
            <p><strong>Billing Period:</strong> {selected.billingPeriod}</p>
            <p><strong>Period:</strong> {formatDateTime(selected.periodStart)} - {formatDateTime(selected.periodEnd)}</p>
            <p><strong>Billing Type:</strong> {BILLING_TYPE_LABELS[selected.billingType] || selected.billingType}</p>
            <p><strong>Credit Term:</strong> {selected.creditTerm} days</p>
            <p>
              <strong>Status:</strong>{" "}
              <Tag color={DISPLAY_STATUS_COLORS[getDisplayStatus(selected)]}>
                {DISPLAY_STATUS_LABELS[getDisplayStatus(selected)]}
              </Tag>
            </p>
            <p><strong>Issued At:</strong> {selected.issuedAt ? formatDateTime(selected.issuedAt) : "-"}</p>
            <p><strong>Due At:</strong> {selected.dueAt ? formatDateTime(selected.dueAt) : "-"}</p>
            <p><strong>Original Amount:</strong> {formatAmount(selected.originalAmount, selected.currency)}</p>
            <p><strong>Paid Amount:</strong> {formatAmount(selected.paidAmount, selected.currency)}</p>
            <p><strong>Offset Amount:</strong> {formatAmount(selected.offsetAmount, selected.currency)}</p>
            <p><strong>Outstanding Amount:</strong> {formatAmount(selected.outstandingAmount, selected.currency)}</p>
            <p><strong>Line Count:</strong> {selected.lineCount}</p>
            {selected.finalisedAt && <p><strong>Finalised At:</strong> {formatDateTime(selected.finalisedAt)}</p>}
            {selected.paidAt && <p><strong>Paid At:</strong> {formatDateTime(selected.paidAt)}</p>}

            <h4 style={{ marginTop: 24 }}>
              <strong>Itemised Charges</strong>
            </h4>
            <Table
              rowKey="consignNo"
              columns={chargeColumns}
              dataSource={statementCharges}
              loading={chargesLoading}
              pagination={false}
              scroll={{ x: true }}
              size="small"
              locale={{ emptyText: "No line items" }}
            />
          </div>
        )}
      </Drawer>

      <Modal
        title="Generate Billing Statement"
        visible={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={handleGenerate}
        confirmLoading={generating}
        okText="Generate"
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <div>Billing Period</div>
            <DatePicker
              picker="month"
              style={{ width: "100%" }}
              value={generatePeriod}
              onChange={setGeneratePeriod}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default UserBillingStatements;
